import { ResizeGroup } from "@fluentui/react";
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { NoHrefLink } from "../../../../components/Links/NoHrefLink/NoHrefLink";
import { useComponentLogger } from "../../../../shared/logging";
import { Pill, Severity } from "../Pill/Pill";

export interface PillContainerBaseProps {
    readonly children: React.ReactNode;

    /** Maximum number of lines that should be revealed when collapsed the list. */
    readonly lines?: number;

    /** Severity level indicating the colour of the pills within the container. Used to render correct value for showMore button */
    readonly severity?: Severity;

    /** BI ID for 'Show More' button. */
    readonly showMoreBtnBiId: string;

    /** String formatter function for aria-label on the 'Show More' pill */
    readonly showMoreAriaLabelFormatter: (numRemaining: number) => string;
}

interface PillContainerShowMoreProps {
    readonly useCustomExpand?: false;

    /** BI ID for 'Show Less' Hyperlink. */
    readonly showLessBiId: string;

    /** Localized text for 'Show Less'. */
    readonly showLessText: string;

    /** aria-label for 'Show Less' link. */
    readonly showLessAriaLabel: string;
}

export interface PillContainerCustomExpandProps {
    /**
     * Set to `true` for a custom callback when the 'Show More' is clicked.
     * Useful when the PillContainer shouldn't do default expansion
     */
    readonly useCustomExpand: true;

    /** Custom callback for when 'Show More' is clicked */
    readonly onShowMore: (event: React.MouseEvent<HTMLElement>) => void;
}

export type PillContainerProps = (PillContainerShowMoreProps | PillContainerCustomExpandProps) & PillContainerBaseProps;

interface PillContainerRenderData {
    readonly cacheKey: string;
    readonly pills: readonly React.ReactNode[];
    readonly remaining: number;
    readonly visible: readonly React.ReactNode[];
}

const isReactElement = (x: React.ReactNode): x is React.ReactElement => {
    return (x as React.ReactElement)?.key !== undefined;
};

const computePillContainerRenderData = (visible: readonly React.ReactNode[], previous: PillContainerRenderData | null): PillContainerRenderData => {
    const keys = visible.map((p, index) => isReactElement(p) ? p.key : `${index}`);
    const pills = previous?.pills || visible;
    const remaining = pills.length - visible.length;

    return { visible, pills, remaining, cacheKey: keys.join("_") };
};

const reduceData = (data: PillContainerRenderData) => {
    return data.visible.length ? computePillContainerRenderData(data.visible.slice(0, -1), data) : undefined;
};

const growData = (data: PillContainerRenderData) => {
    return data.remaining ? computePillContainerRenderData(data.pills.slice(0, data.visible.length + 1), data) : undefined;
};

interface ChildPillContainerWrapperRef {
    readonly setRemaining: (value: number) => void;
}

const ChildPillContainerWrapper = forwardRef<ChildPillContainerWrapperRef, PillContainerProps>(({
    children,
    ...otherProps
}, ref) => {
    const [remaining, setRemaining] = useState(0);

    useImperativeHandle(ref, () => ({ setRemaining }));

    let pills = React.Children.toArray(children);
    pills = pills.slice(pills.length - remaining);

    return <PillContainer children={pills} {...otherProps} />;
});

export const PillContainer: React.FC<PillContainerProps> = ({
    children,
    severity,
    lines = 2, // tslint:disable-line: no-magic-numbers
    showMoreBtnBiId,
    showMoreAriaLabelFormatter,
    ...otherProps
}) => {
    useComponentLogger("PillContainer");

    const childRef = useRef<ChildPillContainerWrapperRef | null>(null);

    const [isShowedMore, setIsShowedMore] = useState(false);
    const showMore = useCallback(() => setIsShowedMore(true), []);
    const showLess = useCallback(() => setIsShowedMore(false), []);

    const renderedData = useCallback((data: PillContainerRenderData) => childRef.current?.setRemaining(data.remaining), []);

    const pills = React.Children.toArray(children);

    const renderData = (data: PillContainerRenderData) => (
        <div style={{ display: "flex" }}>
            {data.visible}
            {
                lines <= 1 && data.remaining ? (
                    <Pill
                        aria-label={showMoreAriaLabelFormatter(data.remaining)}
                        data-bi-id={showMoreBtnBiId}
                        text={`+${data.remaining}`}
                        onClick={otherProps.useCustomExpand ? otherProps.onShowMore : showMore}
                        severity={severity}
                    />
                ) : null
            }
        </div>
    );

    let content = (
        <>
            <ResizeGroup
                data={computePillContainerRenderData(pills, null)}
                onRenderData={renderData}
                onReduceData={reduceData}
                onGrowData={growData}
                dataDidRender={renderedData}
            />
            {
                lines > 1 ? (
                    <ChildPillContainerWrapper
                        ref={childRef}
                        lines={lines - 1}
                        children={pills}
                        showMoreBtnBiId={showMoreBtnBiId}
                        showMoreAriaLabelFormatter={showMoreAriaLabelFormatter}
                        severity={severity}
                        {...otherProps}
                    />
                ) : null
            }
        </>
    );

    if (!pills.length) {
        return null;
    } else if (!otherProps.useCustomExpand) {
        content = (
            <>
                <div style={isShowedMore ? { visibility: "hidden", position: "absolute", width: "100%" } : undefined}>
                    {content}
                </div>
                {
                    isShowedMore && (
                        <>
                            <div style={{ display: "flex", flexFlow: "wrap" }}>
                                {children}
                            </div>
                            <NoHrefLink aria-label={otherProps.showLessAriaLabel} data-bi-id={otherProps.showLessBiId} onClick={showLess}>
                                {otherProps.showLessText}
                            </NoHrefLink>
                        </>
                    )
                }
            </>
        );
    }

    return content;
};
